import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { PlusCircle } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { FormRule } from "../../types/ruleTypes";
import { FormSection } from "../../types/formTypes";
import { InlineRuleItem } from "./InlineRuleItem";
import { JobDocumentTypeEnum } from "../../../../pages/job-page/job-document/job-document.types";

interface RulesListProps {
	rules: FormRule[];
	onAddRule: (rule: FormRule) => void;
	onUpdateRule: (ruleId: string, updates: Partial<FormRule>) => void;
	onDeleteRule: (ruleId: string) => void;
	formSections: FormSection[];
}

const RulesList: React.FC<RulesListProps> = ({
	rules,
	onAddRule,
	onUpdateRule,
	onDeleteRule,
	formSections,
}) => {
	const [newRuleIds, setNewRuleIds] = useState<Set<string>>(new Set());

	const handleAddNewRule = () => {
		const newRule: FormRule = {
			id: uuidv4(),
			name: "",
			enabled: true,
			conditionGroup: {
				type: "and",
				conditions: [],
			},
			action: {
				type: "create_job_document",
				documentType: JobDocumentTypeEnum.Quotation,
				templateId: null,
			},
		};

		onAddRule(newRule);
		setNewRuleIds((prev) => new Set(prev).add(newRule.id));

		// Remove from new rule IDs after a delay (so it doesn't stay marked as new forever)
		setTimeout(() => {
			setNewRuleIds((prev) => {
				const next = new Set(prev);
				next.delete(newRule.id);
				return next;
			});
		}, 500);
	};

	return (
		<div className="w-full max-w-2xl mx-auto my-4">
			<div className="flex justify-between items-center mb-4">
				<h3 className="text-lg font-semibold">Formularregeln</h3>
				<Button
					onClick={handleAddNewRule}
					size="sm"
					className="gap-2"
					data-testid="add-rule-button"
					aria-label="Neue Regel erstellen"
				>
					<PlusCircle className="h-4 w-4" />
					Neue Regel
				</Button>
			</div>

			{rules.length === 0 ? (
				<div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
					<p className="text-gray-500 mb-2">Noch keine Regeln definiert</p>
					<p className="text-sm text-gray-400 mb-4">
						Erstellen Sie Regeln, um automatisch Auftragsdokumente basierend auf den
						Formularantworten zu erstellen.
					</p>
					<Button
						onClick={handleAddNewRule}
						variant="outline"
						className="gap-2"
					>
						<PlusCircle className="h-4 w-4" />
						Erste Regel erstellen
					</Button>
				</div>
			) : (
				<div className="space-y-2">
					{rules.map((rule) => (
						<InlineRuleItem
							key={rule.id}
							rule={rule}
							formSections={formSections}
							isNew={newRuleIds.has(rule.id)}
							onUpdate={onUpdateRule}
							onDelete={onDeleteRule}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default RulesList;
